<template>
    <b-modal
        :visible="true"
        size="lg"
        title="평가 프로파일 비교 실행"
        @hidden="$emit('close')"
    >
        <p class="text-muted">
            같은 문항에 대해 두 프로파일(또는 기본 vs 다른 평가자 기준)로 AI 평가를 실행해
            결과를 비교합니다. 평가는 비동기로 처리되며, 결과는 평가 관리 화면에서 확인합니다.
        </p>

        <b-form>
            <b-row>
                <b-col md="4">
                    <b-form-group label="캠페인 ID">
                        <b-form-input v-model.number="campaignId" type="number" />
                    </b-form-group>
                </b-col>
                <b-col md="4">
                    <b-form-group label="기업 ID">
                        <b-form-input v-model.number="companyId" type="number" />
                    </b-form-group>
                </b-col>
                <b-col md="4">
                    <b-form-group label="문항 코드 (선택)">
                        <b-form-input v-model="subCode" placeholder="비우면 전체 문항 대상" />
                    </b-form-group>
                </b-col>
            </b-row>

            <b-card no-body class="p-3 mb-3">
                <b-row>
                    <b-col md="6">
                        <b-form-group label="프로파일 A (기본/기준)">
                            <b-form-input :value="baseProfile.profile_name" readonly />
                            <small class="text-muted">id: {{ baseProfile.id }} · {{ baseProfile.status }}</small>
                        </b-form-group>
                    </b-col>
                    <b-col md="6">
                        <b-form-group label="프로파일 B (비교 대상)">
                            <b-form-select v-model="compareProfileId" :options="otherProfileOptions" />
                        </b-form-group>
                    </b-col>
                </b-row>
            </b-card>
        </b-form>

        <b-alert :show="!!message" :variant="messageVariant">{{ message }}</b-alert>

        <template #modal-footer>
            <b-button variant="secondary" @click="$emit('close')">닫기</b-button>
            <b-button
                variant="primary"
                :disabled="running || requested || !canRun"
                @click="runCompare"
            >
                <b-spinner v-if="running" small /> {{ requested ? '요청 완료' : '비교 실행' }}
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
export default {
    name: 'EvalProfileCompare',
    props: {
        baseProfile: { type: Object, required: true },
        profiles: { type: Array, default: () => [] },
    },
    data() {
        return {
            campaignId: null,
            companyId: null,
            subCode: '',
            compareProfileId: null,
            running: false,
            requested: false,
            message: '',
            messageVariant: 'info',
        };
    },
    computed: {
        otherProfileOptions() {
            return [
                { value: null, text: '선택...' },
                ...this.profiles
                    .filter((p) => p.id !== this.baseProfile.id)
                    .map((p) => ({
                        value: p.id,
                        text: `${p.profile_name} (${p.status})`,
                    })),
            ];
        },
        canRun() {
            return this.campaignId && this.companyId && this.compareProfileId;
        },
    },
    watch: {
        // 대상이 바뀌면 다시 요청할 수 있다. 같은 대상의 중복 발행만 막는다.
        campaignId() { this.requested = false; },
        companyId() { this.requested = false; },
        subCode() { this.requested = false; },
        compareProfileId() { this.requested = false; },
    },
    methods: {
        async runCompare() {
            // 비용이 드는 AI 평가를 2회 발행한다 — 대상과 범위를 확인받는다.
            // ponytail: 서버 dry_run 건수 미확인(BE 필요) — 건수 대신 입력한 대상을 보여준다.
            const scope = this.subCode
                ? `문항 ${this.subCode}`
                : '문항 코드를 비웠으므로 이 기업의 전체 문항';
            const ok = await this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: '프로파일 비교 실행',
                message: `캠페인 ${this.campaignId} / 기업 ${this.companyId}의 ${scope}에 대해 AI 평가를 2회(프로파일 A, B) 발행합니다. `
                    + '이미 평가된 문항도 다시 평가될 수 있고, AI 비용이 발생하며 요청 후에는 취소할 수 없습니다. 계속하시겠습니까?',
            });
            if (!ok) return;
            this.running = true;
            this.message = '';
            try {
                const basePayload = {
                    campaign_id: this.campaignId,
                    company_id: this.companyId,
                    sub_code: this.subCode || undefined,
                    profile_id: this.baseProfile.id,
                };
                const comparePayload = { ...basePayload, profile_id: this.compareProfileId };

                // 두 프로파일로 각각 평가 실행 (비동기 큐 적재)
                await this.$axios.post('/assessments/ai-audit', basePayload);
                await this.$axios.post('/assessments/ai-audit', comparePayload);

                this.requested = true;
                this.messageVariant = 'success';
                this.message = '두 프로파일로 평가를 요청했습니다. '
                    + '결과는 AI 평가 관리 화면에서 프로파일별로 확인할 수 있습니다.';
            } catch (e) {
                this.messageVariant = 'danger';
                const msg = getErrorMessage(e, '평가 실행 요청에 실패했습니다.');
                this.message = msg;
            } finally {
                this.running = false;
            }
        },
    },
};
</script>
